import { useCallback, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

export type PaginationMeta = {
  current_page: number
  last_page: number
  total: number
}

type UsePaginationProps<T> = {
  loadData: (page: number) => Promise<{ data?: T[] | undefined, meta?: PaginationMeta | undefined } | undefined>
  isActive?: boolean
}

export default function usePagination<T> ({
  loadData,
  isActive = true,
}: UsePaginationProps<T>) {
  const [data, setData] = useState<T[]>([])
  const [meta, setMeta] = useState<PaginationMeta | undefined>()
  const [isFetching, setIsFetching] = useState(false)
  const [page, setPage] = useState(0)

  const hasNextPage = meta === undefined || page < meta.last_page

  const onLoadMore = useCallback(async () => {
    if (!isActive) {
      return
    }

    setIsFetching(true)

    const newPage = page + 1
    setPage(newPage)

    const newData = await loadData(newPage)

    if (newData !== undefined) {
      // Additional check to ensure back-to-back search changes don't
      // result in multiple sets of the same page
      if (newData.meta?.current_page === meta?.current_page) {
        setData(newData.data!)
      } else {
        setData([...data, ...newData.data!])
      }

      setMeta(newData.meta!)

      setIsFetching(false)
    }
  }, [data, loadData, meta?.current_page, page, isActive])

  const reset = useCallback(() => {
    setData([])
    setMeta(undefined)
    setPage(0)
  }, [])

  const [ref] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore,
    rootMargin: '0px 0px 200px 0px',
  })

  return {
    ref,
    data,
    isFetching,
    hasNextPage,
    reset,
  }
}
